import React from 'react';
import { Link } from 'react-router-dom';
import {
    Shield,
    Radio,
    Zap,
    Monitor,
    Server,
    Clock,
    Target,
    ArrowRight,
    CheckCircle2
} from 'lucide-react';
import PageLayout from '../../Layout/PageLayout';
import PageHeader from '../../PageHeader/PageHeader';
import ModernCTA from '../../CTA/ModernCTA';
import './ModernSolutions.scss';

const ModernArmySolutions = () => {
    const products = [
        {
            icon: <Radio size={28} />,
            title: 'Tactical Communication Systems',
            description: 'Advanced field communication networks with encrypted channels and satellite uplinks.',
            badge: 'Field-tested',
            features: ['Encrypted channels', 'Satellite connectivity', 'Weather resistant', 'Long-range capability']
        },
        {
            icon: <Monitor size={28} />,
            title: 'Ruggedized Computing Equipment',
            description: 'Military-grade computers and displays built for extreme environments.',
            badge: 'Combat-ready',
            features: ['Shock resistant', 'Temperature tolerant', 'EMI shielded', 'Field repairable']
        },
        {
            icon: <Server size={28} />,
            title: 'Mobile Command Station Setups',
            description: 'Complete command and control solutions deployable in any terrain.',
            badge: 'Operational',
            features: ['Rapid deployment', 'Modular design', 'Integrated systems', 'Remote operation']
        },
        {
            icon: <Shield size={28} />,
            title: 'Secure Command Post Networks',
            description: 'Hardened network infrastructure for secure military communications.',
            badge: 'Classified',
            features: ['Zero-trust security', 'Mesh networking', 'Quantum encryption', 'Redundant systems']
        }
    ];

    const capabilities = [
        {
            icon: <Zap size={28} />,
            metric: '<4 hrs',
            title: 'Rapid Installation',
            description: 'Complete system deployment in under 4 hours'
        },
        {
            icon: <Clock size={28} />,
            metric: '99.9%',
            title: 'Minimal Downtime',
            description: 'Hot-swappable components ensure continuous operation'
        },
        {
            icon: <Shield size={28} />,
            metric: 'MIL-STD',
            title: 'Battlefield-Ready',
            description: 'MIL-STD tested equipment for extreme conditions'
        },
        {
            icon: <Target size={28} />,
            metric: '99.7%',
            title: 'Mission Success',
            description: 'Proven reliability in active deployments worldwide'
        }
    ];

    const features = [
        { icon: <Shield size={24} />, title: 'Secure', text: 'Military-grade encryption' },
        { icon: <Radio size={24} />, title: 'Connected', text: 'Advanced communication' },
        { icon: <Monitor size={24} />, title: 'Monitored', text: 'Real-time diagnostics' },
        { icon: <Zap size={24} />, title: 'Rapid', text: 'Instant deployment' }
    ];

    return (
        <PageLayout>
            <PageHeader
                title="Army Solutions"
                subtitle="Technology for decisive advantage"
                breadcrumbs={[
                    { label: 'Industries', path: '/serve' },
                    { label: 'Department of Defense', path: '/department' },
                    { label: 'Army Solutions' }
                ]}
            />

            {/* Hero Section */}
            <section className="jk-solutions-hero">
                <div className="jk-solutions-hero__container">
                    <div className="jk-solutions-hero__grid jk-military-hero__grid">
                        <div className="jk-solutions-hero__content">
                            <span className="jk-section-label">
                                <Shield size={16} />
                                U.S. Army Technology Partner
                            </span>
                            <h2 className="jk-solutions-hero__title">
                                Field-Tested Infrastructure for
                                <span className="highlight"> Mission-Critical Operations</span>
                            </h2>
                            <p className="jk-solutions-hero__description">
                                We provide field-tested infrastructure solutions engineered for mission-critical
                                Army operations. Our technology enables decisive advantage through secure
                                communications, ruggedized computing, and rapid deployment capabilities.
                            </p>
                            <div className="jk-solutions-hero__buttons">
                                <Link to="/contact" className="jk-btn jk-btn--primary">
                                    Deploy Solutions
                                    <ArrowRight size={20} />
                                </Link>
                                <Link to="/department" className="jk-btn jk-btn--secondary">
                                    DoD Overview
                                </Link>
                            </div>
                        </div>

                        <div className="jk-solutions-hero__features">
                            {features.map((feature, index) => (
                                <div key={index} className="jk-solutions-hero__feature-card">
                                    <div className="jk-solutions-hero__feature-icon">
                                        {feature.icon}
                                    </div>
                                    <h4 className="jk-solutions-hero__feature-title">{feature.title}</h4>
                                    <p className="jk-solutions-hero__feature-text">{feature.text}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Products Section */}
            <section className="jk-solutions-services">
                <div className="jk-solutions-services__container">
                    <div className="jk-solutions-services__header">
                        <span className="jk-section-label">
                            <Server size={16} />
                            Field-Tested Solutions
                        </span>
                        <h2 className="jk-solutions-services__title">
                            Mission-Critical
                            <span className="highlight"> Infrastructure</span>
                        </h2>
                        <p className="jk-solutions-services__subtitle">
                            Systems engineered for reliability under fire
                        </p>
                    </div>

                    <div className="jk-solutions-services__grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                        {products.map((product, index) => (
                            <div key={index} className="jk-solutions-services__card">
                                <div className="jk-solutions-services__card-header">
                                    <div className="jk-solutions-services__card-icon">
                                        {product.icon}
                                    </div>
                                    <span className="jk-solutions-services__card-badge">{product.badge}</span>
                                </div>
                                <h3 className="jk-solutions-services__card-title">{product.title}</h3>
                                <p className="jk-solutions-services__card-description">{product.description}</p>
                                <ul className="jk-solutions-services__card-features">
                                    {product.features.map((feature, idx) => (
                                        <li key={idx}>
                                            <CheckCircle2 size={12} />
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Capabilities Section */}
            <section className="jk-solutions-capabilities">
                <div className="jk-solutions-capabilities__container">
                    <div className="jk-solutions-capabilities__header">
                        <span className="jk-section-label">
                            <Target size={16} />
                            Deployment Capabilities
                        </span>
                        <h2 className="jk-solutions-capabilities__title">
                            Rapid Installation • Minimal Downtime •
                            <span className="highlight"> Battlefield-Ready</span>
                        </h2>
                    </div>

                    <div className="jk-solutions-capabilities__grid">
                        {capabilities.map((capability, index) => (
                            <div key={index} className="jk-solutions-capabilities__card">
                                <div className="jk-solutions-capabilities__card-icon">
                                    {capability.icon}
                                </div>
                                <div className="jk-solutions-capabilities__card-metric">{capability.metric}</div>
                                <h3 className="jk-solutions-capabilities__card-title">{capability.title}</h3>
                                <p className="jk-solutions-capabilities__card-text">{capability.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <ModernCTA />
        </PageLayout>
    );
};

export default ModernArmySolutions;
